import type { Line } from "./calc.js";
import type { LegKey, Sport } from "./legs.js";

/**
 * Saved plans, on the device.
 *
 * The record shape deliberately mirrors what an API would store, so swapping in
 * a server later is mechanical (docs/TECHSTACK.md).
 *
 * IMPORTANT: everything read back from localStorage is untrusted input. It may
 * be from an older version of the app, hand-edited, or corrupt. It is `unknown`
 * until validated here -- the TypeScript types below describe what we *intend*
 * to have stored, not what we will actually find. Validate at this boundary and
 * nowhere else.
 */

const KEY = "carbdial.plans.v1";

export interface StoredLeg {
  durMinMin: number;
  durMinMax: number;
  bottleMl: number;
  cupMl: number;
  stations: number;
  drinks: Line[];
  gels: Line[];
  bars: Line[];
}

export interface PlanRecord {
  id: string;
  name: string;
  date: string;
  sport: Sport;
  weight: number;
  legs: Partial<Record<LegKey, StoredLeg>>;
  totalCarbs: number;
  perHour: number;
  createdAt: number;
  updatedAt: number;
}

export type NewPlan = Omit<PlanRecord, "id" | "createdAt" | "updatedAt"> & { id?: string };

const SPORTS: readonly string[] = ["bike", "run", "triathlon", "duathlon"];
const LEG_KEYS: readonly string[] = ["swim", "bike", "run", "run2"];

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const num = (v: unknown, fallback = 0): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);

function parseLine(v: unknown): Line | null {
  if (!isObject(v)) return null;
  const kind = str(v["kind"]);
  if (kind !== "drink" && kind !== "gel" && kind !== "bar") return null;
  const id = str(v["id"]);
  if (!id) return null;
  return {
    kind,
    id,
    name: str(v["name"], id),
    carbs: num(v["carbs"]),
    kcal: num(v["kcal"]),
    count: num(v["count"]),
  };
}

function parseLines(v: unknown): Line[] {
  return Array.isArray(v) ? v.map(parseLine).filter((l): l is Line => l !== null) : [];
}

function parseLeg(v: unknown): StoredLeg | null {
  if (!isObject(v)) return null;
  return {
    durMinMin: num(v["durMinMin"], 5),
    durMinMax: num(v["durMinMax"], 5),
    bottleMl: num(v["bottleMl"], 750),
    cupMl: num(v["cupMl"], 100),
    stations: num(v["stations"], 0),
    drinks: parseLines(v["drinks"]),
    gels: parseLines(v["gels"]),
    bars: parseLines(v["bars"]),
  };
}

/** Returns null rather than throwing: one bad record must not lose the others. */
export function parsePlan(v: unknown): PlanRecord | null {
  if (!isObject(v)) return null;
  const id = str(v["id"]);
  const sport = str(v["sport"]);
  if (!id || !SPORTS.includes(sport)) return null;

  const legs: Partial<Record<LegKey, StoredLeg>> = {};
  const rawLegs = v["legs"];
  if (isObject(rawLegs)) {
    for (const key of LEG_KEYS) {
      // Own-property check keeps inherited keys (__proto__ and friends) out.
      if (!Object.prototype.hasOwnProperty.call(rawLegs, key)) continue;
      const leg = parseLeg(rawLegs[key]);
      if (leg) legs[key as LegKey] = leg;
    }
  }

  const updatedAt = num(v["updatedAt"]);
  return {
    id,
    name: str(v["name"], id),
    date: str(v["date"]),
    sport: sport as Sport,
    weight: num(v["weight"], 75),
    legs,
    totalCarbs: num(v["totalCarbs"]),
    perHour: num(v["perHour"]),
    createdAt: num(v["createdAt"], updatedAt),
    updatedAt,
  };
}

function read(): PlanRecord[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(parsePlan)
      .filter((p): p is PlanRecord => p !== null)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    // Corrupt or unavailable storage (private mode, quota). An empty list is a
    // better outcome than a blank screen.
    return [];
  }
}

function write(list: PlanRecord[]): PlanRecord[] {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    // Nothing sensible to do -- the plan still works in memory this session.
  }
  return list;
}

export function allPlans(): PlanRecord[] {
  return read();
}

export function getPlan(id: string): PlanRecord | null {
  return read().find((p) => p.id === id) ?? null;
}

/** Insert or update by id. `now` is injected so tests stay deterministic. */
export function savePlan(plan: NewPlan, now: number = Date.now()): PlanRecord {
  const list = read();
  const id = plan.id ?? `p${now.toString(36)}`;
  const existing = list.findIndex((p) => p.id === id);
  const record: PlanRecord = {
    ...plan,
    id,
    updatedAt: now,
    createdAt: existing >= 0 ? (list[existing]?.createdAt ?? now) : now,
  };
  if (existing >= 0) list[existing] = record;
  else list.unshift(record);
  write(list);
  return record;
}

export function removePlan(id: string): PlanRecord[] {
  return write(read().filter((p) => p.id !== id));
}

export function clearPlans(): void {
  write([]);
}
